import axios from "axios";

export type TierCode = "bronze" | "silver" | "gold" | "platinum";
export interface AffiliateTier { code: TierCode; minimum_sales: number | null; commission_percentage: string | null; }
export interface TierConfiguration { enabled: boolean; revision: number; tiers: AffiliateTier[]; }

export interface AffiliateAccount {
  discount_codes: { code: string; percentage: string }[];
  sales_count: number;
  tier: AffiliateTier | null;
  assigned_tier: AffiliateTier | null;
  earned_tier: AffiliateTier | null;
  next_tier: AffiliateTier | null;
  sales_to_next_tier: number | null;
  tier_assignment: "automatic" | "manual";
  tiers_enabled: boolean;
  id: string;
  display_name: string;
  email: string;
  approval_state: "pending" | "active" | "suspended";
  email_verified_at: string | null;
  commission_percentage: string;
  approved_by_id: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
  referral_code: string | null;
  referral_url: string | null;
}

// Own Axios instance: customer bearer tokens/interceptors and Redux never participate.
export const affiliateHttp = axios.create({ baseURL: "/api/affiliate", withCredentials: true });

function csrfHeaders() {
  const cookie = document.cookie.split("; ").find(value => value.startsWith("wavemind_affiliate_csrf="));
  return { "X-Affiliate-CSRF": cookie ? decodeURIComponent(cookie.substring(cookie.indexOf("=") + 1)) : "" };
}

export const affiliateApi = {
  resolveReferral: async (code: string) =>
    (await affiliateHttp.get<{ referral_code: string }>(`/referrals/${encodeURIComponent(code)}`, { timeout: 5000 })).data,
  register: async (data: { display_name: string; email: string; password: string; password_confirm: string }) =>
    (await affiliateHttp.post<{ message: string }>("/register", data)).data,
  confirm: async (token: string) => (await affiliateHttp.post<{ message: string }>("/confirm-email", { token })).data,
  resend: async (email: string) => (await affiliateHttp.post<{ message: string }>("/resend-confirmation", { email })).data,
  login: async (email: string, password: string) => (await affiliateHttp.post<AffiliateAccount>("/login", { email, password })).data,
  requestReset: async (email: string) => (await affiliateHttp.post<{ message: string }>("/password-reset/request", { email })).data,
  reset: async (token: string, password: string, password_confirm: string) =>
    (await affiliateHttp.post<{ message: string }>("/password-reset/confirm", { token, password, password_confirm })).data,
  me: async () => (await affiliateHttp.get<AffiliateAccount>("/me")).data,
  link: async () => (await affiliateHttp.post<AffiliateAccount>("/referral-link", {}, { headers: csrfHeaders() })).data,
  logout: async () => (await affiliateHttp.post("/logout", {}, { headers: csrfHeaders() })).data,
};

export function affiliateError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (detail && typeof detail.message === "string" && detail.message.trim()) return detail.message;
    if (error.code === "ECONNABORTED" || error.response?.status === 504) {
      return "The server did not respond in time. Please try again.";
    }
    if (!error.response) return "Could not reach WaveMind. Check your internet connection and try again.";
    switch (error.response.status) {
      case 401: return "Your session has ended. Please log in again.";
      case 403: return "This request was not authorized. Reload the page or log in again.";
      case 422: return "Some form fields are invalid. Check your entries and try again.";
      case 429: return "Too many attempts. Please wait 15 minutes before trying again.";
      case 502:
      case 503: return "The server is temporarily unavailable. Please try again shortly.";
      default: return "A server error interrupted this request. Please contact WaveMind support if it continues.";
    }
  }
  return "An unexpected error interrupted this request. Reload the page and try again.";
}

export function affiliateSessionExpired(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}
